import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Paper } from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import SearchIcon from '@mui/icons-material/Search';
import SpeakerNotesIcon from '@mui/icons-material/SpeakerNotes';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import HearingIcon from '@mui/icons-material/Hearing';
import FeedbackReportCard from './FeedbackReportCard';
import { useFeedbackService, FeedbackType } from '../services/feedbackService';

interface FeedbackReportGridProps {
  feedback: FeedbackType | null;
}

const FeedbackReportGrid: React.FC<FeedbackReportGridProps> = ({ feedback, }) => {
  // if (loading) {
  //   return (
  //     <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px" overflow='auto' >
  //       <CircularProgress />
  //     </Box>
  //   );
  // }

  // if (error) {
  //   return (
  //     <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
  //       <Typography color="error">{error}</Typography>
  //     </Box>
  //   );
  // }

  if (!feedback) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Typography>No feedback found</Typography>
      </Box>
    );
  }

  // Calculate overall score (average of category scores)
  const scoreKeys = [
    'listening_score',
    'rapport_score',
    'articulation_score',
    'discovery_score',
    'nextsteps_score',
    'total_score'
  ] as const;

  const overallScore = feedback.total_score

  const overallColor =
    parseFloat(overallScore) >= feedback.high_breakpoint
      ? '#006c04'
      : parseFloat(overallScore) >= feedback.medium_breakpoint
      ? '#4CAF50'
      : parseFloat(overallScore) >= feedback.low_breakpoint
      ? '#FFC107'
      : '#F44336';

  const overallStatus =
    parseFloat(overallScore) >= feedback.high_breakpoint
      ? 'Exceeds Expectations'
      : parseFloat(overallScore) >= feedback.medium_breakpoint
      ? 'Meets Expectations'
      : parseFloat(overallScore) >= feedback.low_breakpoint
      ? 'Improvement Needed'
      : 'Major Improvements Required';

  // Define mapping for feedback categories
  const feedbackCategoryMapping = [
    {
      key: 'listening',
      scoreKey: 'listening_score',
      reasoningKey: 'listening_reasoning',
      title: 'Active Listening',
      icon: <HearingIcon fontSize="large" />,
    },
    {
      key: 'rapport',
      scoreKey: 'rapport_score',
      reasoningKey: 'rapport_reasoning',
      title: 'Rapport',
      icon: <Diversity3Icon fontSize="large" />,
    },
    {
      key: 'articulation',
      scoreKey: 'articulation_score',
      reasoningKey: 'articulation_reasoning',
      title: 'Articulation',
      icon: <SpeakerNotesIcon fontSize="large" />,
    },
    {
      key: 'discovery',
      scoreKey: 'discovery_score',
      reasoningKey: 'discovery_reasoning',
      title: 'Discovery',
      icon: <SearchIcon fontSize="large" />,
    },
    {
      key: 'nextsteps',
      scoreKey: 'nextsteps_score',
      reasoningKey: 'nextsteps_reasoning',
      title: 'Next Steps',
      icon: <ArrowForwardIcon fontSize="large" />,
    },
  ];

  // Other feedback sections (cards)
  const otherSections = feedbackCategoryMapping.map((mapping) => {
    const rawScore = feedback[mapping.scoreKey as keyof FeedbackType];
    const score = Number.isNaN(Number.parseFloat(rawScore as string)) ? 0 : Number.parseFloat(rawScore as string);

    const high = feedback.high_breakpoint
    const medium = feedback.medium_breakpoint
    const low = feedback.low_breakpoint

    type ScoreTier = 'high' | 'medium' | 'low' | 'critical';
    let tier: ScoreTier;
    if      (score >= high)   tier = 'high';
    else if (score >= medium) tier = 'medium';
    else if (score >= low)    tier = 'low';
    else                      tier = 'critical';

    const colorMap: Record<ScoreTier, string> = {
      high:     '#006c04',
      medium:   '#4CAF50',
      low:      '#FFC107',
      critical: '#F44336',
    };

    const statusTypeMap: Record<ScoreTier, 'success' | 'warning' | 'error'> = {
      high:     'success',
      medium:   'success',
      low:      'warning',
      critical: 'error',
    };

    const statusMap: Record<ScoreTier, string> = {
      high:     'Exceeds Expectations',
      medium:   'Meets Expectations',
      low:      'Improvement Needed',
      critical: 'Major Improvements Required',
    };

    const stepMap: Record<ScoreTier, number> = {
      high:     4,
      medium:   3,
      low:      2,
      critical: 1,
    };

    return {
      title: mapping.title,
      icon: mapping.icon,
      color: colorMap[tier],
      status: statusMap[tier],
      statusType: statusTypeMap[tier],
      step: stepMap[tier],
      content: (
        <Typography variant="body2">
          {feedback[mapping.reasoningKey as keyof FeedbackType] as string}
        </Typography>
      ),
    };
  });

  return (
    <Box sx={{ width: '100%', mt: 4, p: 2, overflow: 'auto' }}>
      {/* Overall Score Summary */}
      <Paper
        elevation={3}
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 3,
          mb: 2,
          borderLeft: `8px solid ${overallColor}`,
        }}
      >
        <EmojiEventsIcon sx={{ fontSize: 40, color: overallColor, mr: 2 }} />
        <Box>
          <Typography variant="h6" fontWeight="bold">
            Final Assessment
          </Typography>
          <Typography variant="h5" fontWeight="bold" color={overallColor}>
            {overallStatus}
          </Typography>
        </Box>
      </Paper>

      {/* Actionable Feedback Summary */}
      <Paper
        elevation={3}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          p: 3,
          mb: 2,
          borderLeft: `8px solid ${overallColor}`,
        }}
      >
        <DirectionsRunIcon sx={{ fontSize: 32, color: overallColor, mr: 2, mt: 0.5 }} />
        <Box>
          <Typography variant="h6" fontWeight="bold">
            Actionable Feedback
          </Typography>
          <Typography variant="body2">
            {feedback.actionable_feedback}
          </Typography>
        </Box>
      </Paper>

      {/* Feedback Category Cards */}
      <Box
        sx={{
          display: 'grid',
          justifyItems: 'center',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
          gap: 4,
          width: '100%',
        }}
      >
        {otherSections.map((section) => (
          <FeedbackReportCard
            key={section.title}
            title={section.title}
            icon={section.icon}
            color={section.color}
            status={section.status}
            statusType={section.statusType}
            step={section.step}
          >
            {section.content}
          </FeedbackReportCard>
        ))}
      </Box>
    </Box>
  );
};

export default FeedbackReportGrid;